/**
 * Sprint 23.8 → 26.0 — SetTaskModal.
 *
 * The workspace-level "New task" entry point: pick which agent should own a new
 * task, then land on that agent's profile with the new task added (reuses the
 * `useCreateTask` mutation). v1 keeps it lightweight — a picker + the existing
 * create flow, not a full inline editor.
 */

import { useMemo, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { Avatar, showToast } from "../primitives";
import { getAgentPersona } from "../cockpit/CockpitHeader";
import { useCreateTask } from "../../hooks/use-task-mutations";
import { useListTree } from "../../../lib/hooks/use-kuvio";
import { useAuth } from "../../../lib/hooks/use-auth";
import type { LibraryNode } from "../../../lib/types";

export interface SetTaskModalProps {
  open: boolean;
  onClose: () => void;
}

export function SetTaskModal({ open, onClose }: SetTaskModalProps) {
  const { currentWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const createTask = useCreateTask();
  const tree = useListTree(currentWorkspaceId ?? undefined);

  const agents = useMemo<LibraryNode[]>(
    () => (tree.data ?? []).filter((n) => n.type === "agent" && !n.archived_at),
    [tree.data],
  );

  if (!open) return null;

  function pick(agent: LibraryNode) {
    createTask.mutate(
      { node_id: agent.id, title: "New task" },
      {
        onSuccess: () => {
          onClose();
          showToast(`Added a task to ${agent.name}.`);
          navigate(`/agents/${agent.id}#tasks`);
        },
        onError: (err) =>
          showToast(
            `Couldn't add task: ${err instanceof Error ? err.message : "error"}`,
            "error",
            true,
          ),
      },
    );
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div style={titleRow}>
          <span style={title}>New task</span>
          <button type="button" style={closeBtn} onClick={onClose} aria-label="Close">
            <i className="ti ti-x" aria-hidden="true" />
          </button>
        </div>
        <div style={subtitle}>Which agent should own this task?</div>
        <div style={list}>
          {agents.map((a) => (
            <button
              key={a.id}
              type="button"
              style={agentRow}
              onClick={() => pick(a)}
              disabled={createTask.isPending}
            >
              <Avatar
                persona={getAgentPersona({ path: a.path, agent_config: a.agent_config as Record<string, unknown> | null })}
                name={a.name}
                size="sm"
              />
              <span style={agentName}>{a.name}</span>
              {a.description?.trim() && <span style={agentRole}>{a.description}</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.32)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 300,
};

const card: CSSProperties = {
  width: 420,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "70vh",
  overflowY: "auto",
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-secondary)",
  borderRadius: "var(--border-radius-lg)",
  padding: 16,
  fontFamily: "var(--font-sans)",
  boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
};

const titleRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const title: CSSProperties = {
  fontSize: 15,
  fontWeight: 500,
  color: "var(--color-text-primary)",
};

const closeBtn: CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  fontSize: 16,
  padding: 4,
};

const subtitle: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-secondary)",
  margin: "4px 0 12px",
};

const list: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const agentRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 8px",
  borderRadius: "var(--border-radius-md)",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  textAlign: "left",
  fontFamily: "var(--font-sans)",
  width: "100%",
};

const agentName: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  flexShrink: 0,
};

const agentRole: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
